'use client';

import { c as _c } from "react/compiler-runtime";
import type { VariantProps } from 'class-variance-authority';
import dynamic from 'next/dynamic';
import { type ButtonProps } from '@/app/components/button';
import { type ContentProps, HeaderStyled, type HeaderWithContentProps } from '@/app/components/headers/base';
import { HeaderLevel2 } from '@/app/components/headers/header-level-2';
import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { type ImageProps } from '@/app/components/media/image/types';
import { useOverlayMountControl } from '@/app/components/overlays/hooks/useOverlayMountControl';
import { ImageContent } from '@/app/components/sections/service/service-center/content-media/image-content';
import { ListContent } from '@/app/components/sections/service/service-center/content-media/list-content';
import { OVERLAY_KEYS } from '@/app/constants/overlayKeys';
import { useRevealAnim } from '@/app/hooks/useRevealAnim';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import type { DataGAProps } from '@/app/utils/createDataGA';
const YOUTUBE_INSTANCE_ID = 'service-center-youtube';
const IFrameVideoModal = dynamic(() => import('@/app/components/overlays/modal/video-modal').then(mod => mod.IFrameVideoModal), {
  ssr: false
});
const Content1Styled = cnva('', {
  variants: {
    position: {
      left: 'order-2 lg:order-1',
      right: 'order-1 lg:order-2'
    }
  },
  defaultVariants: {
    position: 'left'
  }
});
const Content2Styled = cnva('', {
  variants: {
    position: {
      left: 'order-1 lg:order-2',
      right: 'order-2 lg:order-1'
    }
  },
  defaultVariants: {
    position: 'left'
  }
});
export type Position = VariantProps<typeof Content1Styled>['position'];
export type ServiceCenterContentMedia = Pick<HeaderWithContentProps, 'theme' | 'layout' | 'lead' | 'title'> & {
  position?: Position;
  description?: string;
  youtubeId?: string;
  buttons?: ButtonProps[];
  image?: Omit<ImageProps, 'aspectRatio'>;
  list?: string[];
};
type SectionContentType = ServiceCenterContentMedia & Omit<HeaderWithContentProps, 'content1' | 'content2' | 'theme'>;
export interface ServiceContentMediaSectionProps extends SectionContentType {
  id?: string;
  className?: string;
  position?: Position;
  content?: ContentProps['content'] | null;
  containerClassName?: string;
  sectionClassName?: string;
  videoButtonClassName?: string;
  list?: string[];
  tracking?: Omit<DataGAProps, 'click'>;
}
export const ServiceCenterContentMedia = t0 => {
  const $ = _c(73);
  let className;
  let containerClassName;
  let content;
  let content2ClassName;
  let contentClassName;
  let id;
  let image;
  let props;
  let sectionClassName;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let tracking;
  let videoButtonClassName;
  let youtubeId;
  if ($[0] !== t0) {
    ({
      id,
      theme: t1,
      position: t2,
      layout: t3,
      image,
      content,
      list: t4,
      className,
      sectionClassName,
      containerClassName,
      contentClassName,
      content2ClassName,
      videoButtonClassName,
      youtubeId,
      testId: t5,
      tracking,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = containerClassName;
    $[3] = content;
    $[4] = content2ClassName;
    $[5] = contentClassName;
    $[6] = id;
    $[7] = image;
    $[8] = props;
    $[9] = sectionClassName;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = tracking;
    $[16] = videoButtonClassName;
    $[17] = youtubeId;
  } else {
    className = $[1];
    containerClassName = $[2];
    content = $[3];
    content2ClassName = $[4];
    contentClassName = $[5];
    id = $[6];
    image = $[7];
    props = $[8];
    sectionClassName = $[9];
    t1 = $[10];
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    tracking = $[15];
    videoButtonClassName = $[16];
    youtubeId = $[17];
  }
  const theme = t1 === undefined ? "light" : t1;
  const position = t2 === undefined ? "left" : t2;
  const layout = t3 === undefined ? "two-column" : t3;
  let t6;
  if ($[18] !== t4) {
    t6 = t4 === undefined ? [] : t4;
    $[18] = t4;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const list = t6;
  const testId = t5 === undefined ? "intro" : t5;
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      defaultBaseDelay: 0.04
    };
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  useRevealAnim(t7);
  const hasList = list.length > 0;
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      overlayKey: OVERLAY_KEYS.MODAL_VIDEO,
      instanceId: YOUTUBE_INSTANCE_ID
    };
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const {
    shouldRender: isOpenModal,
    open: openModal,
    close: closeModal
  } = useOverlayMountControl(t8);
  let t9;
  if ($[22] !== contentClassName || $[23] !== position) {
    t9 = Content1Styled({
      position,
      className: cn("text-gray-700", contentClassName)
    });
    $[22] = contentClassName;
    $[23] = position;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const content1WrapperClassName = t9;
  let t10;
  if ($[25] !== content || $[26] !== hasList || $[27] !== list) {
    t10 = hasList ? <ListContent list={list} /> : content;
    $[25] = content;
    $[26] = hasList;
    $[27] = list;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const renderContent = t10;
  const t11 = id ?? undefined;
  const t12 = `${testId}-section`;
  let t13;
  if ($[29] !== t12) {
    t13 = createDataAttributes({
      test: t12
    });
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== containerClassName || $[32] !== hasList || $[33] !== layout || $[34] !== theme) {
    t14 = cn(HeaderStyled({
      theme,
      layout
    }), containerClassName, {
      "items-center": hasList
    });
    $[31] = containerClassName;
    $[32] = hasList;
    $[33] = layout;
    $[34] = theme;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const t15 = `${testId}-container`;
  let t16;
  if ($[36] !== t15) {
    t16 = createDataAttributes({
      test: t15
    });
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== content2ClassName || $[39] !== position) {
    t17 = Content2Styled({
      position,
      className: content2ClassName
    });
    $[38] = content2ClassName;
    $[39] = position;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== content1WrapperClassName || $[42] !== image || $[43] !== openModal || $[44] !== tracking || $[45] !== videoButtonClassName || $[46] !== youtubeId) {
    t18 = !!image && {
      content1: {
        content: <ImageContent image={image} youtubeId={youtubeId} className={content1WrapperClassName} buttonClassName={videoButtonClassName} openYoutubeModal={openModal} tracking={tracking} />
      }
    };
    $[41] = content1WrapperClassName;
    $[42] = image;
    $[43] = openModal;
    $[44] = tracking;
    $[45] = videoButtonClassName;
    $[46] = youtubeId;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== renderContent) {
    t19 = {
      content: renderContent
    };
    $[48] = renderContent;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== className || $[51] !== content1WrapperClassName || $[52] !== layout || $[53] !== props || $[54] !== t17 || $[55] !== t18 || $[56] !== t19 || $[57] !== theme) {
    t20 = <HeaderLevel2 {...props} data-revealanim={true} theme={theme} layout={layout} className={className} contentClassName={content1WrapperClassName} content2ClassName={t17} {...t18} content2={t19} />;
    $[50] = className;
    $[51] = content1WrapperClassName;
    $[52] = layout;
    $[53] = props;
    $[54] = t17;
    $[55] = t18;
    $[56] = t19;
    $[57] = theme;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== t14 || $[60] !== t16 || $[61] !== t20) {
    t21 = <Container className={t14} {...t16} data-revealanim={true}>{t20}</Container>;
    $[59] = t14;
    $[60] = t16;
    $[61] = t20;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== closeModal || $[64] !== isOpenModal || $[65] !== youtubeId) {
    t22 = !!youtubeId && !!isOpenModal && <IFrameVideoModal title={"\u0E14\u0E39\u0E27\u0E34\u0E14\u0E35\u0E42\u0E2D"} instanceId={YOUTUBE_INSTANCE_ID} youtubeId={youtubeId} onClose={closeModal} />;
    $[63] = closeModal;
    $[64] = isOpenModal;
    $[65] = youtubeId;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== sectionClassName || $[68] !== t11 || $[69] !== t13 || $[70] !== t21 || $[71] !== t22) {
    t23 = <Section id={t11} size="md" className={sectionClassName} {...t13} data-revealanim-group={true}>{t21}{t22}</Section>;
    $[67] = sectionClassName;
    $[68] = t11;
    $[69] = t13;
    $[70] = t21;
    $[71] = t22;
    $[72] = t23;
  } else {
    t23 = $[72];
  }
  return t23;
};